import {
    AllEvents,
    CollectionDataLoaded,
    createCollectionAvailableEvent,
    PublicationCardItem,
} from '@news-mono/web-common'
import React, { ReactElement } from 'react'
import {
    StyledArticlesArticle,
    StyledArticlesContainer,
    StyledArticlesGrid,
    StyledArticlesHeader,
    StyledArticlesHeaderLink,
    StyledArticlesHeaderIcon,
    StyledArticlesHeaderText,
    StyledReadViewAll,
    StyledViewAllText,
} from './TNArticleCards.styled'
import { LandscapeNightly } from '../../cards/LandscapeNightly/LandscapeNightly'
import { useImpressionAvailable } from '../../__helpers/impression-available-helper'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import { useProduct } from '../../__product'
import { IconArrowTopRight } from '../../icons/IconArrowTopRight/IconArrowTopRight'

export interface TNArticleCardsProps {
    data: MaybeLoaded<CollectionDataLoaded>
    onEvent: (event: AllEvents) => void
    sectionHeading?: string
    displayPublicationDate?: boolean
    renderBasicTeaser?: boolean
    hideBorder?: boolean
    headerAs?: React.ElementType
    headerLink?: string
    useSectionColor?: boolean
    displayKicker?: boolean
    icon?: {
        IconElement: ReactElement
    }
}

export const TNArticleCards = ({
    data,
    onEvent,
    displayPublicationDate,
    renderBasicTeaser,
    hideBorder,
    displayKicker,
    icon,
    sectionHeading,
    headerAs,
    headerLink,
    useSectionColor,
}: TNArticleCardsProps) => {
    const product = useProduct()
    const isPublicationCardItem = (
        article: CollectionDataLoaded['publications'][number],
    ): article is PublicationCardItem =>
        article.cardType !== 'marketing-redirect-tile'

    // Fire analytics when viewed.
    const impressionRef = useImpressionAvailable({
        available: () => {
            if (!data.loaded) return
            onEvent(
                createCollectionAvailableEvent(
                    data.result.publications.filter(isPublicationCardItem),
                    'Lima',
                    product,
                    onEvent,
                ),
            )
        },
        loading: !data.loaded,
    })

    if (
        !data.loaded ||
        data.result.publications.filter(isPublicationCardItem).length === 0
    ) {
        return null
    }

    const Header = ({ children }: { children: React.ReactNode }) => {
        if (headerLink) {
            return (
                <StyledArticlesHeaderLink to={headerLink}>
                    {children}
                    <StyledReadViewAll>
                        <StyledViewAllText>View all</StyledViewAllText>
                        <IconArrowTopRight width={18} height={18} />
                    </StyledReadViewAll>
                </StyledArticlesHeaderLink>
            )
        }

        return <StyledArticlesHeader>{children}</StyledArticlesHeader>
    }

    const articles = data.result.publications
    const headerContent = (
        <>
            {icon?.IconElement && (
                <StyledArticlesHeaderIcon>
                    {icon.IconElement}
                </StyledArticlesHeaderIcon>
            )}
            <StyledArticlesHeaderText
                as={headerAs}
                useSectionColor={useSectionColor}
            >
                {sectionHeading}
            </StyledArticlesHeaderText>
        </>
    )

    return (
        <StyledArticlesContainer>
            {sectionHeading && <Header>{headerContent}</Header>}
            <StyledArticlesGrid ref={impressionRef}>
                {articles.filter(isPublicationCardItem).map((article, i) => {
                    return (
                        <StyledArticlesArticle key={article.id}>
                            <LandscapeNightly
                                item={{
                                    loaded: true,
                                    result: article,
                                }}
                                onEvent={onEvent}
                                cardNumber={i + 1}
                                contentPosition="center"
                                displayNumber={false}
                                imagePosition="left"
                                hideByline={false}
                            />
                        </StyledArticlesArticle>
                    )
                })}
            </StyledArticlesGrid>
        </StyledArticlesContainer>
    )
}
